import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './Terminal.module.css';
import { useWindowManager, useDock, WindowId } from '../Window/WindowManager';
import { useWindowResize } from '../Window/useWindowResize';
import { computeWindowDefaults } from '../Window/windowLayout';
import { ResizeHandles } from '../Window/ResizeHandles';
import { WindowControls } from '../Window/WindowControls';
import { executeCommand, type HistoryEntry, type InteractiveSession } from './commands';
import { useFileSystem } from '../Window/FileSystem';
import { useTheme } from '@/design-system';
import { TerminalIcon } from '../icons';

const TYPING_SPEED = 225;
const COMMAND_DELAY = 1000;

const BOOT_COMMANDS = ['help'];

interface TerminalProps {
  onBootComplete?: () => void;
}

export function Terminal({ onBootComplete }: TerminalProps) {
  // Boot sequence state
  const [cmdIndex, setCmdIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'output' | 'waiting' | 'done'>('typing');
  const [bootOutputs, setBootOutputs] = useState<string[][]>([]);

  // Interactive terminal state
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showBoot, setShowBoot] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Interactive session state
  const [interactiveSession, setInteractiveSession] = useState<InteractiveSession | null>(null);
  const [interactiveStep, setInteractiveStep] = useState(0);
  const [interactiveValues, setInteractiveValues] = useState<string[]>([]);
  const [isAwaiting, setIsAwaiting] = useState(false);

  // File system
  const fileSystem = useFileSystem();
  const fileSystemRef = useRef(fileSystem);
  useEffect(() => {
    fileSystemRef.current = fileSystem;
  }, [fileSystem]);

  // Window manager context for open command
  const { openWindow } = useDock();
  const { setTheme, resolvedTheme } = useTheme();

  // Window manager
  const {
    zIndex,
    focus,
    close,
    minimize,
    maximize,
    isMinimized,
    isClosed,
    isMaximized,
  } = useWindowManager(WindowId.Terminal, 'Terminal', <TerminalIcon />);

  // Window resize
  const { size, position, setPosition, isResizing, handleResizeStart } = useWindowResize(
    { width: 640, height: 480 },
    { x: 100, y: 25 },
    { minWidth: 300, minHeight: 180, computeDefaults: computeWindowDefaults(WindowId.Terminal) }
  );

  // Terminal dragging
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const terminalRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!terminalRef.current) return;
    if (isMaximized) return;
    e.preventDefault(); // Prevent focus loss from input
    focus();
    const rect = terminalRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setIsDragging(true);
  }, [focus, isMaximized]);

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e: PointerEvent) => {
      const heroEl = document.querySelector('[data-desktop]') as HTMLElement;
      if (!heroEl) return;

      const heroRect = heroEl.getBoundingClientRect();
      const terminalEl = terminalRef.current;
      if (!terminalEl) return;

      const terminalWidth = terminalEl.offsetWidth;
      const terminalHeight = terminalEl.offsetHeight;

      let newX = e.clientX - heroRect.left - dragOffset.current.x;
      let newY = e.clientY - heroRect.top - dragOffset.current.y;

      const dockHeight = 56;
      newX = Math.max(0, Math.min(newX, heroRect.width - terminalWidth));
      newY = Math.max(0, Math.min(newY, heroRect.height - terminalHeight - dockHeight));

      setPosition({ x: newX, y: newY });
    };

    const handlePointerUp = () => {
      setIsDragging(false);
      // Refocus input after dragging
      if (phase === 'done') {
        inputRef.current?.focus();
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isDragging, setPosition, phase]);

  // Boot sequence typing effect
  useEffect(() => {
    if (phase === 'done') return;

    const currentCmd = BOOT_COMMANDS[cmdIndex];
    if (!currentCmd) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Boot sequence requires cascading state updates
      setPhase('done');
      return;
    }

    if (phase === 'typing') {
      if (charIndex < currentCmd.length) {
        const timer = setTimeout(() => setCharIndex(charIndex + 1), TYPING_SPEED);
        return () => clearTimeout(timer);
      } else {
        // Execute the command and store output
        const result = executeCommand(currentCmd, [], fileSystemRef.current);
        const output = Array.isArray(result.output) ? result.output : result.output ? [result.output] : [];
        setBootOutputs((prev) => [...prev, output]);
        setPhase('output');
      }
    } else if (phase === 'output') {
      const hasNext = cmdIndex < BOOT_COMMANDS.length - 1;
      if (hasNext) {
        setPhase('waiting');
      } else {
        const timer = setTimeout(() => setPhase('done'), 500);
        return () => clearTimeout(timer);
      }
    } else if (phase === 'waiting') {
      const timer = setTimeout(() => {
        setCmdIndex(cmdIndex + 1);
        setCharIndex(0);
        setPhase('typing');
      }, COMMAND_DELAY);
      return () => clearTimeout(timer);
    }
  }, [cmdIndex, charIndex, phase]);

  // Notify parent when boot completes
  useEffect(() => {
    if (phase === 'done' && onBootComplete) {
      onBootComplete();
    }
  }, [phase, onBootComplete]);

  // Focus input when boot completes or interactive session ends
  useEffect(() => {
    if (phase === 'done' && !isAwaiting) {
      inputRef.current?.focus();
    }
  }, [phase, isAwaiting]);

  // Scroll to bottom when history changes or interactive state changes
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [history, interactiveStep, isAwaiting]);

  // Handle command submission
  const handleSubmit = useCallback(() => {
    const trimmedInput = inputValue.trim();

    // Interactive session mode
    if (interactiveSession) {
      // Record this step's value in history as a prompt line
      setHistory((prev) => [
        ...prev,
        { command: `${interactiveSession.steps[interactiveStep].prompt} ${trimmedInput}`, output: [], noHistory: true },
      ]);

      const newValues = [...interactiveValues, trimmedInput];
      setInteractiveValues(newValues);
      setInputValue('');

      if (interactiveStep < interactiveSession.steps.length - 1) {
        // More steps to go
        setInteractiveStep(interactiveStep + 1);
      } else {
        // Final step — call onComplete
        setIsAwaiting(true);
        if (interactiveSession.pendingMessage) {
          setHistory((prev) => [
            ...prev,
            { command: '', output: [interactiveSession.pendingMessage!], noHistory: true },
          ]);
        }

        interactiveSession.onComplete(newValues).then((result) => {
          const outputLines = Array.isArray(result.output)
            ? result.output
            : result.output
              ? [result.output]
              : [];

          setHistory((prev) => {
            // Remove the pending message entry if there was one
            const updated = interactiveSession.pendingMessage
              ? prev.slice(0, -1)
              : prev;
            return [...updated, { command: '', output: outputLines, isError: result.isError, noHistory: true }];
          });

          setInteractiveSession(null);
          setInteractiveStep(0);
          setInteractiveValues([]);
          setIsAwaiting(false);
        });
      }
      return;
    }

    // Empty input — show a blank prompt line but exclude from arrow-key history
    if (!trimmedInput) {
      setHistory((prev) => [...prev, { command: '', output: [], noHistory: true }]);
      setInputValue('');
      setHistoryIndex(-1);
      return;
    }

    const result = executeCommand(trimmedInput, history, fileSystem, {
      openWindow,
      setTheme,
      resolvedTheme,
    });

    // Handle clear command
    if (result.output === '__CLEAR__') {
      setHistory([]);
      setInputValue('');
      setHistoryIndex(-1);
      setShowBoot(false);
      return;
    }

    // Convert output to array
    const outputLines = Array.isArray(result.output)
      ? result.output
      : result.output
        ? [result.output]
        : [];

    // Add to history
    const newEntry: HistoryEntry = {
      command: trimmedInput || '',
      output: outputLines,
      isError: result.isError,
    };

    setHistory((prev) => [...prev, newEntry]);
    setInputValue('');
    setHistoryIndex(-1);

    // Start interactive session if command returned one
    if (result.interactive) {
      setInteractiveSession(result.interactive);
      setInteractiveStep(0);
      setInteractiveValues([]);
    }
  }, [inputValue, history, fileSystem, interactiveSession, interactiveStep, interactiveValues, openWindow, setTheme, resolvedTheme]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSubmit();
      } else if (e.key === 'c' && e.ctrlKey) {
        e.preventDefault();
        if (interactiveSession) {
          setInteractiveSession(null);
          setInteractiveStep(0);
          setInteractiveValues([]);
          setHistory((prev) => [...prev, { command: '^C', output: [] }]);
        }
        setInputValue('');
        setHistoryIndex(-1);
      } else if (!interactiveSession) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const commandHistory = history.filter((h) => h.command && !h.noHistory);
          if (commandHistory.length === 0) return;

          const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
          setHistoryIndex(newIndex);
          setInputValue(commandHistory[newIndex].command);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          const commandHistory = history.filter((h) => h.command && !h.noHistory);
          if (historyIndex === -1) return;

          const newIndex = historyIndex + 1;
          if (newIndex >= commandHistory.length) {
            setHistoryIndex(-1);
            setInputValue('');
          } else {
            setHistoryIndex(newIndex);
            setInputValue(commandHistory[newIndex].command);
          }
        }
      }
    },
    [handleSubmit, history, historyIndex, interactiveSession]
  );

  // Focus input when clicking terminal body (but not when selecting text)
  const handleBodyClick = useCallback(() => {
    if (phase === 'done') {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) return;
      inputRef.current?.focus();
    }
  }, [phase]);

  // Close animation
  const [isClosing, setIsClosing] = useState(false);

  // Handle button clicks
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClosing(true);
  };

  const handleAnimationEnd = useCallback((e: React.AnimationEvent) => {
    if (e.animationName === styles['windowClose']) {
      setIsClosing(false);
      close();
    }
  }, [close]);

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    minimize();
  };

  const handleMaximize = (e: React.MouseEvent) => {
    e.stopPropagation();
    maximize();
  };

  if (isClosed || isMinimized) {
    return null;
  }

  const windowStyle = isMaximized
    ? {
        left: 0,
        top: 0,
        width: '100%',
        height: 'calc(100% - 56px)',
        zIndex,
      }
    : {
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex,
      };

  // Determine prompt prefix
  const promptPrefix = interactiveSession
    ? interactiveSession.steps[interactiveStep].prompt
    : '>';

  return (
    <div
      ref={terminalRef}
      className={`${styles.terminal} ${isDragging || isResizing ? styles.dragging : ''} ${isMaximized ? styles.maximized : ''} ${isClosing ? styles.closing : ''}`}
      style={windowStyle}
      onPointerDown={focus}
      onAnimationEnd={handleAnimationEnd}
    >
      {!isMaximized && <ResizeHandles onResizeStart={handleResizeStart} />}
      <div className={styles.terminalHeader} onPointerDown={handlePointerDown}>
        <WindowControls
          onClose={handleClose}
          onMinimize={handleMinimize}
          onMaximize={handleMaximize}
        />
        <span className={styles.terminalTitle}>bash</span>
      </div>
      <div ref={bodyRef} className={styles.terminalBody} onClick={handleBodyClick}>
        {/* Boot sequence: completed commands */}
        {showBoot && BOOT_COMMANDS.slice(0, phase === 'done' ? BOOT_COMMANDS.length : cmdIndex).map((cmd, i) => (
          <div key={`boot-${i}`}>
            <div className={`${styles.terminalLine} ${styles.command}`}>
              {'> '}
              {cmd}
            </div>
            {bootOutputs[i]?.map((line, j) => (
              <div key={j} className={`${styles.terminalLine} ${styles.output}`}>{line}</div>
            ))}
          </div>
        ))}

        {/* Boot sequence: current command being typed */}
        {showBoot && phase !== 'done' && BOOT_COMMANDS[cmdIndex] && (
          <>
            <div className={`${styles.terminalLine} ${styles.command}`}>
              {'> '}
              {BOOT_COMMANDS[cmdIndex].slice(0, charIndex)}
              {phase === 'typing' && <span className={styles.cursor}>▋</span>}
            </div>
            {(phase === 'output' || phase === 'waiting') &&
              bootOutputs[cmdIndex]?.map((line, j) => (
                <div key={j} className={`${styles.terminalLine} ${styles.output}`}>{line}</div>
              ))}
          </>
        )}

        {/* Interactive history */}
        {phase === 'done' &&
          history.map((entry, i) => (
            <div key={`hist-${i}`}>
              {(entry.command !== '' || entry.output.length === 0) && (
                <div className={`${styles.terminalLine} ${styles.command}`}>
                  {entry.command.startsWith('^') || !entry.command.includes(':') ? '> ' : ''}
                  {entry.command}
                </div>
              )}
              {entry.output.map((line, j) => (
                <div
                  key={j}
                  className={`${styles.terminalLine} ${entry.isError ? styles.error : styles.output}`}
                >
                  {line}
                </div>
              ))}
            </div>
          ))}

        {/* Interactive input line */}
        {phase === 'done' && !isAwaiting && (
          <div className={`${styles.terminalLine} ${styles.command} ${styles.inputLine}`}>
            <span>{promptPrefix}&nbsp;</span>
            <input
              ref={inputRef}
              type="text"
              className={styles.terminalInput}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}
